import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { Globe, ExternalLink } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { GET_PUBLIC_SITE, UPDATE_PUBLIC_SITE } from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { Loader } from '../common/PageLoader';

/**
 * Public site settings — the handle your collection is served under and whether
 * it is live at all.
 *
 * The slug is validated here for shape only; uniqueness is the server's call
 * (two people can be editing at once), so a taken slug comes back as a
 * CONFLICT and is surfaced verbatim rather than guessed at up front.
 */

const inputClassName =
  'w-full px-3 py-2 bg-form-dark text-cmp-ink rounded border border-cmp-line-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,62}$/;


export function PublicSiteSettings() {
  const { accountId, isLoading: accountsLoading } = useAccount();

  const [slug, setSlug] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  // Write-only: the server never sends a password back, only whether one is
  // set. Empty input means "leave it alone", not "clear it" — clearing is an
  // explicit button, so a routine title edit cannot unlock a site by accident.
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState<'ok' | 'error'>('ok');

  const { data, loading, refetch } = useQuery(GET_PUBLIC_SITE, {
    variables: { accountId },
    skip: !accountId,
  });

  const [updatePublicSite, { loading: saving }] = useMutation(UPDATE_PUBLIC_SITE, {
    onCompleted: () => {
      setMessageTone('ok');
      setMessage('Saved.');
      refetch();
    },
    onError: (err) => {
      setMessageTone('error');
      setMessage(err.message);
    },
  });

  useEffect(() => {
    const a = data?.account;
    if (!a) return;
    setSlug(a.publicSlug || '');
    setEnabled(Boolean(a.publicEnabled));
    setTitle(a.publicTitle || '');
    setTagline(a.publicTagline || '');
    setPassword('');
  }, [data]);

  const slugValid = !slug || SLUG_RE.test(slug);
  // The server knows the live address (custom domain, platform host or
  // {slug}.tinycms.ai). While a new handle is being typed, show where it will be.
  const savedSlug = data?.account?.publicSlug || '';
  const url =
    slug && slug === savedSlug
      ? data?.account?.publicSiteUrl || null
      : slug
        ? `https://${slug}.tinycms.ai`
        : null;
  const protectedNow = Boolean(data?.account?.publicAccessProtected);

  const handleSave = async () => {
    if (!accountId) return;
    setMessage('');
    if (!slugValid) {
      setMessageTone('error');
      setMessage('The handle may use lowercase letters, digits and hyphens only.');
      return;
    }
    // Turning a site on without a handle would publish it at no address.
    if (enabled && !slug) {
      setMessageTone('error');
      setMessage('Choose a handle before making the site public.');
      return;
    }
    await updatePublicSite({
      variables: {
        accountId,
        input: {
          publicSlug: slug || null,
          publicEnabled: enabled,
          publicTitle: title || null,
          publicTagline: tagline || null,
          // Omitted entirely when blank, so the gate survives unrelated edits.
          ...(password ? { publicAccessPassword: password } : {}),
        },
      },
    });
  };

  /**
   * Opening the site is its own action rather than "save with an empty field",
   * because taking a gate down by accident is not recoverable by putting it
   * back — whoever saw the site in between still saw it.
   */
  const clearPassword = async () => {
    if (!accountId) return;
    if (
      !window.confirm('Remove the password? Anyone with the address will be able to see this site.')
    ) {
      return;
    }
    setMessage('');
    setPassword('');
    await updatePublicSite({
      variables: { accountId, input: { publicAccessPassword: '' } },
    });
  };

  if (accountsLoading || loading) return <Loader label="Loading" className="p-6 text-cmp-ink-2" />;

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-lg font-semibold text-cmp-ink flex items-center gap-2">
          <Globe className="w-5 h-5" /> Public site
        </h2>
        <p className="text-sm text-cmp-ink-2 mt-1">
          Publishes the items you have marked published, showing only the fields you marked
          &ldquo;show publicly&rdquo; in Custom Fields. Storage locations and purchase prices are
          never included.
        </p>
      </div>

      <Card className="p-4 space-y-4 bg-cmp-raise border border-cmp-line text-cmp-ink">
        <div>
          <Label htmlFor="publicSlug">Handle</Label>
          <Input
            id="publicSlug"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="bluelinefab"
            className={inputClassName}
          />
          {!slugValid && (
            <p className="text-xs text-red-400 mt-1">
              Lowercase letters, digits and hyphens; 2–63 characters.
            </p>
          )}
          {url && slugValid && (
            <p className="text-xs text-cmp-ink-2 mt-1 flex items-center gap-1">
              <span>{url}</span>
              {enabled && (
                <a href={url} target="_blank" rel="noreferrer" className="underline">
                  <ExternalLink className="w-3 h-3 inline" />
                </a>
              )}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="publicTitle">Site title</Label>
          <Input
            id="publicTitle"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bluelinefab Cards"
            className={inputClassName}
          />
        </div>

        <div>
          <Label htmlFor="publicTagline">Tagline</Label>
          <Input
            id="publicTagline"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="Baseball cards, catalogued."
            className={inputClassName}
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-cmp-ink">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          Site is live
          {enabled ? (
            <Badge className="ml-1 text-[10px]">public</Badge>
          ) : (
            <Badge variant="outline" className="ml-1 text-[10px]">
              off
            </Badge>
          )}
        </label>
        <p className="text-xs text-cmp-ink-2 -mt-2">
          Turning this off takes the site down immediately — published state is checked on every
          read, so nothing stays cached behind it.
        </p>

        <div className="pt-2 border-t border-cmp-line">
          <Label htmlFor="publicAccessPassword">
            Visitor password{' '}
            {protectedNow ? (
              <Badge className="ml-1 text-[10px]">on</Badge>
            ) : (
              <Badge variant="outline" className="ml-1 text-[10px]">
                open
              </Badge>
            )}
          </Label>
          <Input
            id="publicAccessPassword"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={protectedNow ? 'Leave blank to keep the current password' : 'No password'}
            className={inputClassName}
            autoComplete="new-password"
          />
          <p className="text-xs text-cmp-ink-2 mt-1">
            {protectedNow
              ? 'Visitors must enter this password before they see anything. Leave blank to keep it as it is.'
              : 'Your site is open to anyone. Set a password to keep it private while you build it out.'}
          </p>
          {protectedNow && (
            <button
              type="button"
              onClick={() => void clearPassword()}
              disabled={saving}
              className="text-xs text-cmp-ink-2 underline hover:text-cmp-ink mt-2"
            >
              Remove the password and open the site
            </button>
          )}
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {message && (
            <span
              className={messageTone === 'ok' ? 'text-sm text-green-400' : 'text-sm text-red-400'}
            >
              {message}
            </span>
          )}
        </div>
      </Card>
    </div>
  );
}
